/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Single entry for zoneless template-state updates that used to fork into
 * three dialects:
 *   - bufferTemplateFlag  (stale boolean across the current CD turn)
 *   - deferMarkForCheck   (apply + markForCheck on the next macrotask)
 *   - writeViewSignal(..., defer) (host/signal write, optionally deferred)
 *
 * Prefer edit-page phase (`attachEditPagePhase` + viewWrite) on resource edit
 * pages; use these helpers for getters, modals, and non-phased hosts.
 */
import {ChangeDetectorRef, WritableSignal, signal} from '@angular/core';

import {deferMarkForCheck} from './defer-mark-for-check';
import {isViewSignal, readViewSignal, writeViewSignal} from './view-signal';

export type BufferedValue<T> = {display: T; last: T | undefined};
export type BufferedFlag = BufferedValue<boolean>;
export type BufferedSignalValue<T> = {display: WritableSignal<T>; last: T};

export interface BufferTemplateFlagOptions {
  /** Skip the deferred display commit when false (destroyed hosts). */
  isAlive?: () => boolean;
  /** After display updates — typically markForCheck / scheduleViewRefresh. */
  onCommit?: () => void;
}

export function createBufferedFlag(initial = false): BufferedFlag {
  return createBufferedValue(initial);
}

export function createBufferedValue<T>(initial: T): BufferedValue<T> {
  return {display: initial, last: undefined};
}

/** Signal-backed template buffer that schedules its own zoneless refresh. */
export function createBufferedSignalValue<T>(initial: T): BufferedSignalValue<T> {
  return {display: signal(initial), last: initial};
}

/**
 * Buffer a boolean across CD so async hydrate cannot NG0100 *ngIf / [disabled]
 * bindings. Getters should return this on every CD turn.
 */
export function bufferTemplateFlag(
  state: BufferedFlag,
  next: boolean,
  opts?: BufferTemplateFlagOptions
): boolean {
  return bufferTemplateValue(state, next, opts);
}

/**
 * Template getter helper: keep `loaded` / similar flags stale through the
 * current CD turn, then markForCheck. Use for [resolved] / *ngIf bindings.
 */
export function readBufferedFlag(
  state: BufferedFlag,
  next: boolean,
  cdr: ChangeDetectorRef,
  isAlive?: () => boolean
): boolean {
  return bufferTemplateFlag(state, !!next, {
    isAlive,
    onCommit: () => cdr.markForCheck()
  });
}

/** Buffer any primitive template value across the current CD cycle. */
export function bufferTemplateValue<T>(
  state: BufferedValue<T>,
  next: T,
  opts?: BufferTemplateFlagOptions
): T {
  if (state.last === undefined) {
    state.last = next;
    state.display = next;
    return state.display;
  }
  if (next !== state.last) {
    state.last = next;
    // Macrotask — keep the stale display value through the current CD cycle.
    // Plain assignment: signal.set during template render throws NG0600.
    setTimeout(() => {
      if (opts?.isAlive && !opts.isAlive()) {
        return;
      }
      state.display = next;
      opts?.onCommit?.();
    }, 0);
  }
  return state.display;
}

/**
 * Keep the previous signal value through the current verification pass, then
 * publish the new value on the next task. Reading display here registers the
 * template dependency, so no ChangeDetectorRef notification is required.
 */
export function bufferTemplateSignalValue<T>(
  state: BufferedSignalValue<T>,
  next: T,
  isAlive?: () => boolean
): T {
  if (next !== state.last) {
    state.last = next;
    setTimeout(() => {
      if (!isAlive || isAlive()) {
        state.display.set(next);
      }
    }, 0);
  }
  return state.display();
}

/** Alias kept for discoverability beside bufferTemplateFlag / writeHostView. */
export function deferViewWrite(cdr: ChangeDetectorRef, apply: () => void): void {
  deferMarkForCheck(cdr, apply);
}

/** Defer signal-backed state past the current template verification pass. */
export function deferSignalStateWrite(apply: () => void): void {
  setTimeout(apply, 0);
}

/**
 * Transitional write boundary for legacy pages whose templates still read
 * mutable fields. The revision signal must be read by the template.
 */
export function deferSignalViewWrite(revision: WritableSignal<number>, apply: () => void): void {
  deferSignalStateWrite(() => {
    apply();
    revision.update((value) => value + 1);
  });
}

/** Notify a template-read revision signal after a safe synchronous mutation. */
export function notifySignalView(revision: WritableSignal<number>): void {
  revision.update((value) => value + 1);
}

/**
 * Write a host property or WritableSignal. Prefer `defer: true` + `cdr` when
 * writing during an existing CD turn; plain `writeViewSignal(..., true)` has
 * no markForCheck and is legacy-compatible only.
 */
export function writeHostView<T>(
  host: any,
  key: string,
  next: T,
  opts?: {defer?: boolean; cdr?: ChangeDetectorRef}
): void {
  const apply = () => writeViewSignal(host, key, next, false);
  if (opts?.defer) {
    if (opts.cdr) {
      deferMarkForCheck(opts.cdr, apply);
      return;
    }
    writeViewSignal(host, key, next, true);
    return;
  }
  apply();
}

export {
  deferMarkForCheck,
  isViewSignal,
  readViewSignal,
  writeViewSignal
};
